import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    AlertTriangle,
    Copy,
    LayoutGrid,
    Loader2,
    Mail,
    MessageCircle,
    RefreshCw,
    Scale,
    ShieldCheck,
    Sunrise,
    Users,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AiPageShell from "../../components/ai/AiPageShell";
import { AiMarkdown } from "../../components/ai/AiMarkdown";
import {
    BrifingDataTable,
    BrifingSection,
    ONCELIK_COLUMNS,
    OPERASYON_COLUMNS,
    TAHKIM_ADAY_COLUMNS,
    TAHKIM_GECIS_COLUMNS,
} from "../../components/ai/GunlukBrifingTables";
import { akilGunlukBrifing } from "../../lib/akilApi";

const TABS = [
    { id: "tahkim", label: "Tahkim", icon: Scale },
    { id: "oncelik", label: "Öncelik", icon: AlertTriangle },
    { id: "operasyon", label: "Operasyon", icon: LayoutGrid },
    { id: "brifing", label: "AI Brifing", icon: Sunrise },
];

function KpiTile({ label, value, hint, tone = "gold" }) {
    const ring =
        tone === "red"
            ? "border-rose-500/35 bg-rose-500/[0.06]"
            : tone === "purple"
              ? "border-purple-500/35 bg-purple-500/[0.06]"
              : tone === "amber"
                ? "border-amber-500/35 bg-amber-500/[0.06]"
                : "border-[#D4AF37]/25 bg-[#121A2F]/80";
    return (
        <div className={`rounded-xl border p-4 ${ring}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="font-display text-2xl text-white mt-1">{value ?? 0}</div>
            {hint ? <div className="text-[11px] text-slate-500 mt-1">{hint}</div> : null}
        </div>
    );
}

function buildShareText(ozet, veriler) {
    const s = veriler?.ozet_sayilar || {};
    const t = veriler?.tahkim_ozet || {};
    const header = `MBS Hukuk — Günlük Brifing (${veriler?.tarih || ""} ${veriler?.saat || ""})`;
    const stats = [
        `Açık: ${s.acik_dosya ?? 0}`,
        `Tahkim aktif: ${t.aktif ?? 0}`,
        `Tahkim aday: ${t.aday_15plus ?? 0}`,
        `Eksik evraklı: ${s.eksik_evrakli ?? 0}`,
    ].join(" · ");
    return `${header}\n${stats}\n\n${(ozet || "").replace(/\*\*/g, "")}`;
}

export default function AkilGunlukBrifingPage() {
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("tahkim");
    const [ozet, setOzet] = useState("");
    const [veriler, setVeriler] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await akilGunlukBrifing();
            if (!data.success) throw new Error(data.error);
            setOzet(data.ozet || "");
            setVeriler(data.veriler || {});
        } catch (err) {
            const msg =
                err.response?.data?.error ||
                (err.code === "ECONNABORTED" ? "Brifing zaman aşımı — tekrar deneyin." : err.message);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const stats = veriler?.ozet_sayilar || {};
    const tahkimOzet = veriler?.tahkim_ozet || {};
    const shareText = useMemo(() => buildShareText(ozet, veriler), [ozet, veriler]);

    const copyBrifing = async () => {
        try {
            await navigator.clipboard.writeText(shareText);
            toast.success("Brifing panoya kopyalandı.");
        } catch {
            toast.error("Kopyalama başarısız.");
        }
    };

    const mailBrifing = () => {
        const subject = encodeURIComponent(`MBS Günlük Brifing — ${veriler?.tarih || ""}`);
        const body = encodeURIComponent(shareText);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    };

    const tahkimUyarilar = veriler?.tahkim_uyarilar || {};
    const uyariRows = useMemo(() => {
        const rows = [];
        (tahkimUyarilar.gun_30 || []).forEach((r) => rows.push({ ...r, uyari_band: "30+" }));
        (tahkimUyarilar.gun_21 || []).forEach((r) => rows.push({ ...r, uyari_band: "21+" }));
        (tahkimUyarilar.gun_14 || []).forEach((r) => rows.push({ ...r, uyari_band: "15+" }));
        return rows;
    }, [tahkimUyarilar]);

    const oncelikRows = useMemo(() => {
        const p = veriler?.oncelik?.oncelik || {};
        return [...(p.acil || []), ...(p.bugun || []), ...(p.takip || [])].slice(0, 25);
    }, [veriler]);

    return (
        <AiPageShell
            wide
            title="Günlük Brifing & Sistem Taraması"
            subtitle="Tahkim geçişleri, öncelik kuyruğu ve operasyon tabloları — tek ekranda net görünüm."
            actions={
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={copyBrifing}
                        disabled={!ozet}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/15 text-xs text-slate-300 hover:text-white disabled:opacity-40"
                    >
                        <Copy className="h-3.5 w-3.5" /> Kopyala
                    </button>
                    <button
                        type="button"
                        onClick={mailBrifing}
                        disabled={!ozet}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/15 text-xs text-slate-300 hover:text-white disabled:opacity-40"
                    >
                        <Mail className="h-3.5 w-3.5" /> E-posta
                    </button>
                    <button
                        type="button"
                        onClick={copyBrifing}
                        disabled={!ozet}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-500/30 text-xs text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40"
                    >
                        <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                    </button>
                    <button
                        type="button"
                        onClick={load}
                        disabled={loading}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#D4AF37]/30 text-xs text-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-50"
                    >
                        {loading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <RefreshCw className="h-3.5 w-3.5" />
                        )}
                        Yenile
                    </button>
                </div>
            }
        >
            {loading && !veriler ? (
                <div className="flex items-center justify-center py-24 text-slate-400 gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" /> Sistem taranıyor…
                </div>
            ) : (
                <div className="space-y-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-xs text-slate-500">
                            Son tarama:{" "}
                            <span className="text-slate-300">
                                {veriler?.tarih} {veriler?.saat}
                            </span>
                        </p>
                        <Link
                            to="/yeni/muvekkil-merkezi"
                            className="text-xs text-purple-300 hover:text-purple-200 border border-purple-500/30 px-3 py-1.5 rounded-lg"
                        >
                            Tahkim Akıl Merkezi →
                        </Link>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                        <KpiTile label="Açık dosya" value={stats.acik_dosya} hint={`/${stats.toplam_dosya || 0} toplam`} />
                        <KpiTile label="Tahkim aktif" value={tahkimOzet.aktif} tone="purple" hint="Tahkime geçmiş" />
                        <KpiTile label="Yeni tahkim" value={tahkimOzet.yeni_gecis} tone="purple" hint="Son 30 gün" />
                        <KpiTile label="Tahkim adayı" value={tahkimOzet.aday_15plus} tone="amber" hint="15+ gün başvuru" />
                        <KpiTile label="Eksik evrak" value={stats.eksik_evrakli} tone="amber" hint="7'li liste" />
                        <KpiTile label="Acil öncelik" value={stats.oncelik_acil} tone="red" />
                    </div>

                    <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-[#0B0F19]/80 border border-white/10 w-fit">
                        {TABS.map(({ id, label, icon: Icon }) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => setTab(id)}
                                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${
                                    tab === id
                                        ? "bg-[#D4AF37] text-black"
                                        : "text-slate-400 hover:text-white hover:bg-white/5"
                                }`}
                            >
                                <Icon className="h-3.5 w-3.5" />
                                {label}
                            </button>
                        ))}
                    </div>

                    {tab === "tahkim" && (
                        <div className="space-y-4">
                            <BrifingSection
                                accent="tahkim"
                                title="Tahkime geçen / tahkimde dosyalar"
                                subtitle="Aktif tahkim hattı — yeni geçişler ve paket durumu"
                                action={
                                    <Link
                                        to="/e/sigorta-dosyalari"
                                        className="text-xs text-purple-300 hover:underline"
                                    >
                                        Tüm dosyalar →
                                    </Link>
                                }
                            >
                                <BrifingDataTable
                                    columns={TAHKIM_GECIS_COLUMNS}
                                    rows={veriler?.tahkim_gecis_dosyalar || []}
                                    emptyText="Tahkim durumunda dosya yok."
                                />
                            </BrifingSection>

                            <div className="grid lg:grid-cols-2 gap-4">
                                <BrifingSection
                                    accent="alert"
                                    title="Tahkime geçmesi gerekenler (15+ gün)"
                                    subtitle="Başvuruda bekleyen — otomatik tahkim adayı"
                                >
                                    <BrifingDataTable
                                        columns={TAHKIM_ADAY_COLUMNS}
                                        rows={veriler?.tahkim_aday_tablo || []}
                                        emptyText="15+ gün bekleyen başvuru yok."
                                        compact
                                    />
                                </BrifingSection>

                                <BrifingSection
                                    accent="alert"
                                    title="Zaman aşımı uyarıları"
                                    subtitle="30 / 21 / 14 gün eşikleri"
                                >
                                    <BrifingDataTable
                                        columns={TAHKIM_ADAY_COLUMNS}
                                        rows={uyariRows}
                                        emptyText="Zaman aşımı uyarısı yok."
                                        compact
                                    />
                                </BrifingSection>
                            </div>
                        </div>
                    )}

                    {tab === "oncelik" && (
                        <div className="space-y-4">
                            <BrifingSection
                                title="Öncelik kuyruğu — skorlu aksiyon listesi"
                                subtitle="Acil, bugün ve takip bandları birleşik tablo"
                            >
                                <BrifingDataTable
                                    columns={ONCELIK_COLUMNS}
                                    rows={oncelikRows}
                                    emptyText="Öncelikli dosya yok."
                                />
                            </BrifingSection>

                            {veriler?.kritik_uyarilar?.length ? (
                                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
                                    <div className="flex items-center gap-2 text-amber-200 text-sm font-semibold mb-2">
                                        <AlertTriangle className="h-4 w-4" /> Kritik notlar
                                    </div>
                                    <ul className="text-sm text-amber-100/90 space-y-1 list-disc list-inside">
                                        {veriler.kritik_uyarilar.map((u, i) => (
                                            <li key={i}>{u.replace(/^[^\w]+/, "").trim()}</li>
                                        ))}
                                    </ul>
                                </div>
                            ) : null}
                        </div>
                    )}

                    {tab === "operasyon" && (
                        <div className="space-y-4">
                            {veriler?.portal_onay?.bekleyen_adet ? (
                                <BrifingSection
                                    accent="alert"
                                    title={`Portal evrak onayı — ${veriler.portal_onay.bekleyen_adet} bekliyor`}
                                    action={
                                        <Link
                                            to="/yeni/muvekkil-merkezi"
                                            className="text-xs text-[#D4AF37] hover:underline flex items-center gap-1"
                                        >
                                            <ShieldCheck className="h-3 w-3" /> Merkeze git
                                        </Link>
                                    }
                                >
                                    <BrifingDataTable
                                        columns={OPERASYON_COLUMNS}
                                        rows={veriler.portal_onay.items || []}
                                    />
                                </BrifingSection>
                            ) : null}

                            <div className="grid lg:grid-cols-2 gap-4">
                                <BrifingSection
                                    title="Bugün yeni dosya"
                                    action={
                                        <Link to="/e/sigorta-dosyalari" className="text-xs text-slate-500 hover:text-[#D4AF37]">
                                            Liste →
                                        </Link>
                                    }
                                >
                                    <BrifingDataTable
                                        columns={OPERASYON_COLUMNS}
                                        rows={veriler?.yeni_dosyalar || []}
                                        emptyText="Bugün yeni dosya yok."
                                        compact
                                    />
                                </BrifingSection>

                                <BrifingSection
                                    title="Eksik evraklı dosyalar (7'li)"
                                    action={
                                        <Link to="/yeni/muvekkil-merkezi" className="text-xs text-slate-500 hover:text-[#D4AF37]">
                                            Merkez →
                                        </Link>
                                    }
                                >
                                    <BrifingDataTable
                                        columns={[
                                            ...OPERASYON_COLUMNS.slice(0, 2),
                                            {
                                                key: "eksik",
                                                label: "Eksik",
                                                render: (row) => (
                                                    <span className="text-xs text-amber-300">
                                                        {row.eksik_sayisi ?? row.status ?? "—"}
                                                    </span>
                                                ),
                                            },
                                        ]}
                                        rows={veriler?.eksik_evrakli_dosyalar || []}
                                        emptyText="Eksik evraklı dosya yok."
                                        compact
                                    />
                                </BrifingSection>

                                <BrifingSection title="Bekleyen sigorta başvuruları">
                                    <BrifingDataTable
                                        columns={OPERASYON_COLUMNS}
                                        rows={veriler?.bekleyen_basvurular || []}
                                        emptyText="Bekleyen başvuru yok."
                                        compact
                                    />
                                </BrifingSection>

                                <BrifingSection title="Bugünkü ödemeler">
                                    <BrifingDataTable
                                        columns={OPERASYON_COLUMNS}
                                        rows={veriler?.bugun_odemeler || []}
                                        emptyText="Bugün ödeme hareketi yok."
                                        compact
                                    />
                                </BrifingSection>
                            </div>

                            {veriler?.durum_dagilimi?.length ? (
                                <BrifingSection title="Dosya durum dağılımı">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="text-[10px] uppercase text-slate-500 border-b border-white/10">
                                                    <th className="text-left px-4 py-2">Durum</th>
                                                    <th className="text-right px-4 py-2">Adet</th>
                                                    <th className="text-right px-4 py-2 hidden sm:table-cell">Oran</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {veriler.durum_dagilimi.map((d) => (
                                                    <tr key={d.durum} className="border-t border-white/5">
                                                        <td className="px-4 py-2 text-slate-200">
                                                            {d.durum_label || d.durum}
                                                        </td>
                                                        <td className="px-4 py-2 text-right font-display text-[#F3E5AB]">
                                                            {d.adet}
                                                        </td>
                                                        <td className="px-4 py-2 text-right text-slate-500 text-xs hidden sm:table-cell">
                                                            {stats.toplam_dosya
                                                                ? `${Math.round((100 * d.adet) / stats.toplam_dosya)}%`
                                                                : "—"}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </BrifingSection>
                            ) : null}
                        </div>
                    )}

                    {tab === "brifing" && (
                        <div className="space-y-4">
                            <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/10 to-[#121A2F]/90 p-6">
                                <div className="flex items-center gap-2 text-[#F3E5AB] text-sm font-semibold mb-4">
                                    <Sunrise className="h-5 w-5" /> Akıl günlük brifing metni
                                </div>
                                <div className="prose prose-invert prose-sm max-w-none">
                                    <AiMarkdown text={ozet || "Özet üretilemedi."} />
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <BrifingSection title="Bugün yeni müvekkil" action={<Users className="h-4 w-4 text-slate-500" />}>
                                    <BrifingDataTable
                                        columns={OPERASYON_COLUMNS}
                                        rows={veriler?.yeni_muvekkiller || []}
                                        compact
                                    />
                                </BrifingSection>
                                <BrifingSection title="Son değer kaybı hesapları">
                                    <BrifingDataTable
                                        columns={OPERASYON_COLUMNS}
                                        rows={veriler?.deger_kaybi || []}
                                        compact
                                    />
                                </BrifingSection>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </AiPageShell>
    );
}
